import { getProductQuery } from '@setvi/shared/services';
import { useQuery } from '@tanstack/react-query';
import { Product } from '@setvi/shared/interfaces';
import { useCallback, useEffect, useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { CompanyProductUserType } from '@setvi/shared/services/react-query/query/user/types';
import { useSnackbar } from 'notistack';
import { getSingleSalesKitByName } from 'Services';
import {
  AnalyticsModules,
  SalesEnablementActionType,
  useAnalytics
} from '@setvi/shared/hooks';
import { toTitleCase } from '@setvi/shared/utils/formatCustomerData';

import { useCustomer } from 'pages/sales/modules/orders/subpages/orders/subpages/order-create/use-customer';
import { DetailsDialogProps } from '..';
import { useSalesKitStore } from '../store';

export const useProductDetails = ({
  selectedProduct,
  selectedCustomerId,
  userType
}: Pick<
  DetailsDialogProps,
  'selectedCustomerId' | 'userType' | 'selectedProduct'
>) => {
  const flags = useFlags();

  const [product, setProduct] = useState<Product>();
  const [isLoading, setIsLoading] = useState(true);
  const { enqueueSnackbar } = useSnackbar();

  const { captureAnalytics } = useAnalytics();
  const { customer } = useCustomer({
    customerId: selectedCustomerId
  });

  const { data, isFetching } = useQuery({
    ...getProductQuery({ productId: selectedProduct?._id }),
    enabled: !!selectedProduct?._id
  });

  const fetchProduct = useCallback(
    async (p: Product) => {
      setIsLoading(true);
      try {
        if (!selectedProduct) return;

        const getProductPrice = () => {
          if (userType === CompanyProductUserType.Manufacturer) return p.price;

          if (Number.isFinite(selectedProduct.price))
            return selectedProduct.price;

          return null;
        };

        setProduct({
          ...p,
          name: toTitleCase(p.name),
          ...(p.vendor && {
            vendor: {
              ...p.vendor,
              name: toTitleCase(p.vendor.name)
            }
          }),
          price: getProductPrice(),
          ...(Number.isFinite(selectedProduct.cost)
            ? { cost: selectedProduct.cost }
            : {})
        });
      } catch (e) {
        enqueueSnackbar('Error occurred while fetching product data.', {
          variant: 'error',
          autoHideDuration: 8000
        });
      } finally {
        setIsLoading(false);
      }
    },
    [enqueueSnackbar, selectedProduct, userType]
  );

  const updateDialogProduct = (newValues: Partial<Product>) =>
    setProduct({ ...product, ...newValues });

  useEffect(() => {
    if (!isFetching) {
      data ? fetchProduct(data) : setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProduct, data, isFetching]);

  // Fetch sales enablement content

  const {
    data: salesKit,
    isFetching: isSalesKitFetching,
    error: salesKitError
  } = useQuery({
    ...getSingleSalesKitByName(
      selectedProduct?._id || product?._id,
      'AI Overview'
    ),
    enabled:
      (!!selectedProduct?._id || !!product?._id) && flags?.salesEnablement,
    retry: false
  });

  const { setSalesKit, setSalesKitError, setSalesKitLoading } =
    useSalesKitStore();

  useEffect(() => {
    setSalesKitLoading(isSalesKitFetching);

    if (salesKit) {
      setSalesKit(salesKit);
    }

    if (salesKitError) {
      const msg = (salesKitError as Error).message;
      setSalesKitError(msg);
      captureAnalytics(
        {
          error: msg || '',
          productSKU: product?.sku || selectedProduct?.sku,
          actionType: SalesEnablementActionType.Error
        },
        AnalyticsModules.SalesEnablement
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    salesKit,
    isSalesKitFetching,
    salesKitError,
    setSalesKit,
    setSalesKitLoading,
    setSalesKitError
  ]);

  return {
    product,
    customer,
    salesKit,
    isLoading: isLoading || isFetching,
    salesKitError,
    isSalesKitFetching,
    updateDialogProduct
  };
};
